/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { memo, ReactElement } from 'react';
import { Typography } from '@mui/material';
import { TableCell, TableRow } from '@mui/material';
import { Order } from '../../../data/api/apiCommon.typings';
import { ListTableColumnConfig } from './ListTypes';
import { StyledTableSortLabel } from '../../StyledComponents/StyledTableSortLabel';
import { TableTooltip } from '../../StyledComponents/TableTooltip';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { createListStyles } from './ListStyles';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        headCell: {
            borderBottomColor: theme.palette.text.primary,
            borderBottom: '1px solid',
            backgroundColor: theme.palette.mainBackground.light,
        },
        caption: {
            width: '100%',
        },
        label: {
            color: theme.palette.text.secondary,
            fontWeight: 600,
        },
        icon: {
            backgroundColor: theme.palette.primary.main,
            height: 25,
            width: 5,
            borderRadius: 5,
        },
    }),
);

interface ListTableHeadContentProps<T extends object> {
    columns: ListTableColumnConfig<T>[];
    stateSortParam: string;
    sortingCallback(order: Order, keyForValue: keyof T): void;
    getCustomCellContent?(index: number, columnKey: string): ReactElement;
}

const ListTableHeadContent = <T extends object>(props: ListTableHeadContentProps<T>) => {
    const ListStyles = makeStyles((theme: Theme) => createStyles(createListStyles(theme)));
    const { t } = useTranslation();
    const classes = useStyles();
    const commonClasses = ListStyles();
    const { columns, stateSortParam, sortingCallback, getCustomCellContent } = props;

    const currentKey: keyof T = stateSortParam.split(',')[0] as keyof T;
    const currentOrder: Order = stateSortParam.split(',')[1] as Order;

    const handleSortRequest = (newKey: keyof T) => {
        sortingCallback(currentOrder === 'desc' && newKey === currentKey ? 'asc' : 'desc', newKey);
    };

    const getHeadCellClasses = (index: number): string => {
        if (index === 0) return `${classes.headCell} ${commonClasses.firstCell}`;
        else if (index === columns.length - 1) return `${classes.headCell} ${commonClasses.lastCell}`;
        else return `${classes.headCell} ${commonClasses.middleCell}`;
    };

    return (
        <TableRow>
            {columns.map((column, index) => {
                return column.displayCustomHeader && getCustomCellContent ? (
                    getCustomCellContent(index, column.keyForValue as string)
                ) : column.displayAsColorBar ? (
                    <TableTooltip key={index} title={t(column.translatePath)} placement="right">
                        <TableCell className={classes.headCell}>
                            <div className={classes.icon} />
                        </TableCell>
                    </TableTooltip>
                ) : column.isHidden ? null : (
                    <TableCell
                        key={index}
                        className={getHeadCellClasses(index)}
                        align={column.align}
                        style={{
                            minWidth: column.minWidth,
                            maxWidth: column.maxWidth,
                            width: column.width,
                        }}
                    >
                        {!column.isSortable ? (
                            <Typography className={classes.label} variant={'subtitle2'} noWrap>
                                {t(column.translatePath)}
                            </Typography>
                        ) : (
                            <StyledTableSortLabel
                                className={classes.caption}
                                active={currentKey === column.keyForValue}
                                direction={currentKey === column.keyForValue ? currentOrder : 'asc'}
                                onClick={() => handleSortRequest(column.keyForValue)}
                            >
                                <Typography className={classes.label} variant={'subtitle2'} noWrap>
                                    {t(column.translatePath)}
                                </Typography>
                            </StyledTableSortLabel>
                        )}
                    </TableCell>
                );
            })}
        </TableRow>
    );
};

export default memo(ListTableHeadContent);
